import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";
import Cookies from "js-cookie";
import { store } from "@/store/store";
import { refreshAccessToken, logoutUser } from "@/store/slices/authSlice";

interface ApiErrorResponseData {
  message?: string | string[];
  error?: string;
  statusCode?: number;
  [key: string]: unknown;
}

export interface EnrichedAxiosError extends AxiosError<ApiErrorResponseData> {
  isCanceled: boolean;
  isNetworkError: boolean;
  isClientError: boolean;
  isServerError: boolean;
  userMessage: string;
}

interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const MAX_RETRIES = 2;
const BASE_RETRY_DELAY_MS = 300;
const REQUEST_TIMEOUT_MS = 30_000; // 30 s global default

const TOKEN_EXPIRED_MESSAGES = [
  "token expired",
  "invalid token",
  "malformed token",
  "jwt malformed",
  "jwt expired",
  "unauthorized",
  "authentication required",
] as const;

const RETRYABLE_STATUS_CODES = new Set([408, 429, 502, 503, 504]);
const AUTH_RECOVERY_EXCLUDED_PATHS = new Set(["/auth/refresh", "/auth/logout"]);
const FORCED_LOGOUT_COOKIE = "forced_logout";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffMs(attempt: number): number {
  const base = BASE_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
  const jitter = Math.floor(Math.random() * 100);
  return base + jitter;
}

function extractUserMessage(error: AxiosError<ApiErrorResponseData>): string {
  const data = error.response?.data;

  if (data) {
    const msg = data.message;
    if (Array.isArray(msg) && msg.length > 0) return msg[0];
    if (typeof msg === "string" && msg.length > 0) return msg;
    if (typeof data.error === "string" && data.error.length > 0)
      return data.error;
    if (error.response?.statusText) return error.response.statusText;
  }

  if (error.code === "ECONNABORTED")
    return "Request timed out. Please try again.";
  if (error.code === "ERR_NETWORK")
    return "Network error. Check your connection.";
  if (error.code === "ERR_CANCELED") return "Request was cancelled.";

  return error.message || "An unexpected error occurred.";
}

function enrichError(raw: unknown): EnrichedAxiosError | Error {
  if (!isAxiosError(raw)) {
    return raw instanceof Error ? raw : new Error(String(raw));
  }

  const err = raw as AxiosError<ApiErrorResponseData>;
  const status = err.response?.status;

  const enriched = err as EnrichedAxiosError;
  enriched.isCanceled =
    err.code === "ERR_CANCELED" ||
    err.message === "canceled" ||
    err.name === "CanceledError";
  enriched.isNetworkError = !err.response && !enriched.isCanceled;
  enriched.isClientError =
    status !== undefined && status >= 400 && status < 500;
  enriched.isServerError =
    status !== undefined && status >= 500 && status < 600;
  enriched.userMessage = extractUserMessage(err);

  return enriched;
}

function isTokenExpiredError(error: AxiosError<ApiErrorResponseData>): boolean {
  const raw = error.response?.data?.message ?? "";
  const text = (Array.isArray(raw) ? (raw[0] ?? "") : raw).toLowerCase();
  return TOKEN_EXPIRED_MESSAGES.some((m) => text.includes(m));
}

function isRetryable(error: unknown): boolean {
  if (!isAxiosError(error)) return false;

  if (!error.response) return true;

  const { status } = error.response;
  if (RETRYABLE_STATUS_CODES.has(status)) return true;

  if (status >= 500 && status < 600) return true;

  return false;
}

function normalizeRequestPath(url?: string): string | null {
  if (!url) return null;

  try {
    return new URL(url, "http://localhost").pathname;
  } catch {
    return null;
  }
}

function shouldBypassAuthRecovery(config?: AxiosRequestConfig): boolean {
  const path = normalizeRequestPath(config?.url);
  if (!path) return false;

  // Check if the path is exactly one of the excluded paths
  if (AUTH_RECOVERY_EXCLUDED_PATHS.has(path)) return true;

  // Also check if the path ends with any of the excluded paths (handles /api or /v1 prefixes)
  return Array.from(AUTH_RECOVERY_EXCLUDED_PATHS).some(
    (excluded) => path.endsWith(excluded) && (path.length === excluded.length || path[path.length - excluded.length - 1] === "/"),
  );
}

function setForcedLogoutCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${FORCED_LOGOUT_COOKIE}=1; Path=/; Max-Age=300; SameSite=Lax`;
}

class ApiClient {
  private readonly client: AxiosInstance;

  private isRefreshing = false;

  private failedQueue: Array<{
    resolve: () => void;
    reject: (reason: unknown) => void;
  }> = [];

  private readonly controllers = new Set<AbortController>();

  constructor() {
    this.client = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
      timeout: REQUEST_TIMEOUT_MS,
      headers: { "Content-Type": "application/json" },
      withCredentials: true, // Required to send/receive httpOnly cookies
    });

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  private setupRequestInterceptor(): void {
    this.client.interceptors.request.use(
      (config) => {
        // 1. Add Organization Slug header
        if (typeof window !== "undefined") {
          try {
            const raw = localStorage.getItem("currentOrganization");
            if (raw) {
              const org = JSON.parse(raw) as { slug?: string };
              if (org?.slug) {
                config.headers["x-organization-slug"] = org.slug;
              }
            }
          } catch {
            // Ignore parsing errors
          }
        }

        // 2. Add CSRF token for state-changing requests
        const stateChangingMethods = ["post", "put", "delete", "patch"];
        if (
          config.method &&
          stateChangingMethods.includes(config.method.toLowerCase())
        ) {
          const csrfToken = Cookies.get("XSRF-TOKEN");
          if (csrfToken) {
            config.headers["x-csrf-token"] = csrfToken;
          }
        }

        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }

        return config;
      },
      (error: unknown) => Promise.reject(enrichError(error)),
    );
  }

  private setupResponseInterceptor(): void {
    this.client.interceptors.response.use(
      (response) => response,

      async (error: unknown) => {
        if (!isAxiosError(error)) {
          return Promise.reject(enrichError(error));
        }

        const axiosError = error as AxiosError<ApiErrorResponseData>;
        const originalRequest = axiosError.config as
          | RetryableAxiosRequestConfig
          | undefined;

        // Handle request cancellation
        if (
          axiosError.code === "ERR_CANCELED" ||
          axiosError.name === "CanceledError"
        ) {
          return Promise.reject(enrichError(axiosError));
        }

        if (!originalRequest) {
          return Promise.reject(enrichError(axiosError));
        }

        if (
          axiosError.response?.status === 401 &&
          shouldBypassAuthRecovery(originalRequest)
        ) {
          return Promise.reject(enrichError(axiosError));
        }

        // Handle 401 with token expired - attempt refresh
        if (
          axiosError.response?.status === 401 &&
          !originalRequest._retry &&
          isTokenExpiredError(axiosError)
        ) {
          if (this.isRefreshing) {
            return new Promise<AxiosResponse>((resolve, reject) => {
              this.failedQueue.push({
                resolve: () => resolve(this.client(originalRequest)),
                reject,
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            await store.dispatch(refreshAccessToken()).unwrap();
            this.drainQueue(null);
            return this.client(originalRequest);
          } catch (refreshError: unknown) {
            this.drainQueue(refreshError);
            await this.handleUnrecoverableAuthFailure();
            return Promise.reject(enrichError(refreshError));
          } finally {
            this.isRefreshing = false;
          }
        }

        // Handle other 401 errors (not token expired) - logout immediately
        if (axiosError.response?.status === 401 && !originalRequest._retry) {
          await this.handleUnrecoverableAuthFailure();
          return Promise.reject(enrichError(axiosError));
        }

        return Promise.reject(enrichError(axiosError));
      },
    );
  }

  private drainQueue(error: unknown): void {
    if (error !== null) {
      this.failedQueue.forEach(({ reject }) => reject(error));
    } else {
      this.failedQueue.forEach(({ resolve }) => resolve());
    }
    this.failedQueue = [];
  }

  /**
   * Handles unrecoverable authentication failures
   * Logs out the user and redirects to the public home page
   * This prevents stale tokens from allowing unauthorized access
   */
  private async handleUnrecoverableAuthFailure(): Promise<void> {
    // Cancel all pending requests immediately
    this.cancelAllRequests();

    try {
      // Dispatch logout action to clear Redux state and localStorage
      await store.dispatch(logoutUser());
    } catch (logoutError: unknown) {
      console.error("[ApiClient] Logout dispatch failed:", logoutError);
    }

    setForcedLogoutCookie();

    // Force redirect to the public home page and replace history so users
    // cannot navigate back into protected routes after a 401.
    if (typeof window !== "undefined") {
      window.location.replace("/");
    }
  }

  private async executeWithRetry<T>(
    fn: () => Promise<AxiosResponse<T>>,
    maxRetries = MAX_RETRIES,
  ): Promise<AxiosResponse<T>> {
    let attempt = 0;

    while (true) {
      try {
        return await fn();
      } catch (error: unknown) {
        const enriched = enrichError(error);

        if (
          enriched instanceof Error &&
          "isCanceled" in enriched &&
          (enriched as EnrichedAxiosError).isCanceled
        ) {
          throw enriched;
        }

        const canRetry = isRetryable(error) && attempt < maxRetries;

        if (!canRetry) {
          throw enriched;
        }

        attempt += 1;
        await sleep(backoffMs(attempt));
      }
    }
  }

  private attachController(
    config?: AxiosRequestConfig,
  ): [AxiosRequestConfig, AbortController] {
    const controller = new AbortController();
    this.controllers.add(controller);

    const callerSignal = (
      config as (AxiosRequestConfig & { signal?: AbortSignal }) | undefined
    )?.signal;

    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort(callerSignal.reason);
      } else {
        callerSignal.addEventListener(
          "abort",
          () => controller.abort(callerSignal.reason),
          { once: true },
        );
      }
    }

    const merged: AxiosRequestConfig = {
      ...(config ?? {}),
      signal: controller.signal,
    };

    return [merged, controller];
  }

  private detachController(controller: AbortController): void {
    this.controllers.delete(controller);
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const [mergedConfig, controller] = this.attachController(config);
    try {
      const response = await this.executeWithRetry<T>(() =>
        this.client.get<T>(url, mergedConfig),
      );
      return response.data;
    } finally {
      this.detachController(controller);
    }
  }

  async post<T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const [mergedConfig, controller] = this.attachController(config);
    try {
      const response = await this.executeWithRetry<T>(() =>
        this.client.post<T>(url, data, mergedConfig),
      );
      return response.data;
    } finally {
      this.detachController(controller);
    }
  }

  async put<T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const [mergedConfig, controller] = this.attachController(config);
    try {
      const response = await this.executeWithRetry<T>(() =>
        this.client.put<T>(url, data, mergedConfig),
      );
      return response.data;
    } finally {
      this.detachController(controller);
    }
  }

  async patch<T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const [mergedConfig, controller] = this.attachController(config);
    try {
      const response = await this.executeWithRetry<T>(() =>
        this.client.patch<T>(url, data, mergedConfig),
      );
      return response.data;
    } finally {
      this.detachController(controller);
    }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const [mergedConfig, controller] = this.attachController(config);
    try {
      const response = await this.executeWithRetry<T>(() =>
        this.client.delete<T>(url, mergedConfig),
      );
      return response.data;
    } finally {
      this.detachController(controller);
    }
  }

  async postFormData<T>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const [mergedConfig, controller] = this.attachController(config);
    try {
      const response = await this.executeWithRetry<T>(() =>
        this.client.post<T>(url, formData, mergedConfig),
      );
      return response.data;
    } finally {
      this.detachController(controller);
    }
  }

  async patchFormData<T>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const [mergedConfig, controller] = this.attachController(config);
    try {
      const response = await this.executeWithRetry<T>(() =>
        this.client.patch<T>(url, formData, mergedConfig),
      );
      return response.data;
    } finally {
      this.detachController(controller);
    }
  }

  public cancelAllRequests(): void {
    this.controllers.forEach((c) => {
      try {
        c.abort();
      } catch {
        // Ignore errors during abort
      }
    });
    this.controllers.clear();
  }

  public isCanceledError(error: unknown): boolean {
    if (isAxiosError(error)) {
      return (
        error.code === "ERR_CANCELED" ||
        error.name === "CanceledError" ||
        error.message === "canceled"
      );
    }
    return false;
  }

  public isNetworkError(error: unknown): boolean {
    return (
      isAxiosError(error) && !error.response && !this.isCanceledError(error)
    );
  }

  public isEnrichedError(error: unknown): error is EnrichedAxiosError {
    return isAxiosError(error) && "userMessage" in error;
  }
}

export const apiClient = new ApiClient();
